import { Grid, Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';

// Header indicator for reports loading in the background on the reports page.
// Shows "Reports x/y ready" with a hover list of each report's state.
// Renders nothing on pages without reports (plan is empty there).

const STATUS_LABEL = {
  ready: 'Ready',
  slow: 'Ready (still drawing)',
  loading: 'Loading…',
  error: 'Could not load',
  queued: 'Waiting to load',
  onDemand: 'Loads when opened',
};

const STATUS_COLOR = {
  ready: '#6fcf97',
  slow: '#6fcf97',
  loading: '#f7901d',
  error: '#ff8a80',
  queued: '#bdbdbd',
  onDemand: '#8a8a8a',
};

// Per-report background loading state, shared by the sidebar (dot beside each
// report) and the header pill. `statusFor(id)` is undefined for reports that
// are not part of the background plan.
const useReportPreloadRows = () => {
  const { plan, maxKept, statusById } = useSelector(
    (state) => state.reportPreload,
  );
  // Reports without a live iframe: the first ones (up to the kept limit) are
  // waiting their turn; the rest load only when opened.
  const liveCount = Object.keys(statusById).length;
  let freePlaces = Math.max(maxKept - liveCount, 0);
  const rows = (plan || []).map((report) => {
    let status = statusById[report.id];
    if (!status) {
      if (freePlaces > 0) {
        status = 'queued';
        freePlaces -= 1;
      } else {
        status = 'onDemand';
      }
    }
    return { ...report, status };
  });
  const target = Math.min(rows.length, maxKept);
  const readyCount = rows.filter(
    (r) => r.status === 'ready' || r.status === 'slow',
  ).length;
  const shownReady = Math.min(readyCount, target);
  const byId = new Map(rows.map((r) => [r.id, r.status]));
  return {
    plan: plan || [],
    maxKept,
    rows,
    target,
    shownReady,
    statusFor: (id) => byId.get(id),
  };
};

// Set to false to hide the header pill (the sidebar dots keep working).
const SHOW_HEADER_PILL = true;

const ReportPreloadStatus = () => {
  const { plan, maxKept, rows, target, shownReady } = useReportPreloadRows();
  // The pill is for admins only.
  const isAdmin = Boolean(useSelector((state) => state.PowerBi.user?.ISADMIN));
  if (!SHOW_HEADER_PILL || !isAdmin) return null;
  if (!plan || plan.length === 0) return null;

  const loadingNow = rows.some((r) => r.status === 'loading');

  // Group the rows by section name, keeping sidebar order.
  const sectionMap = new Map();
  rows.forEach((r) => {
    const key = r.section || 'Other';
    if (!sectionMap.has(key)) sectionMap.set(key, []);
    sectionMap.get(key).push(r);
  });
  const sections = [...sectionMap.entries()];

  const tooltip = (
    <div style={{ display: 'grid', gap: 4, padding: '2px 0', maxWidth: 320 }}>
      <div style={{ fontWeight: 600, marginBottom: 2 }}>
        Reports loaded in the background
      </div>
      {sections.map(([sectionName, sectionRows]) => (
        <div key={sectionName} style={{ display: 'grid', gap: 3 }}>
          <div
            style={{
              fontSize: '0.7rem',
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              opacity: 0.7,
              marginTop: 4,
            }}
          >
            {sectionName}
          </div>
          {sectionRows.map((r) => (
            <div
              key={r.id}
              style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}
            >
              <span
                aria-hidden='true'
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: STATUS_COLOR[r.status],
                  flex: '0 0 auto',
                  transform: 'translateY(-1px)',
                }}
              />
              <span style={{ flex: 1 }}>{r.name}</span>
              <span style={{ opacity: 0.8, whiteSpace: 'nowrap' }}>
                {STATUS_LABEL[r.status]}
              </span>
            </div>
          ))}
        </div>
      ))}
      {plan.length > maxKept && (
        <div style={{ opacity: 0.75, marginTop: 4 }}>
          Up to {maxKept} reports are kept loaded to limit memory use.
        </div>
      )}
    </div>
  );

  return (
    <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title={tooltip} arrow>
        <span
          tabIndex={0}
          aria-label={`Reports ready: ${shownReady} of ${target}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '2px 10px',
            borderRadius: 999,
            border: '1px solid rgba(255,255,255,0.25)',
            color: '#ffffff',
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
            cursor: 'default',
            lineHeight: 1.6,
          }}
        >
          <span
            aria-hidden='true'
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: loadingNow ? '#f7901d' : '#6fcf97',
            }}
          />
          Reports {shownReady}/{target} ready
        </span>
      </Tooltip>
    </Grid>
  );
};

export { STATUS_COLOR, STATUS_LABEL, useReportPreloadRows };
export default ReportPreloadStatus;
